import { useEffect, useState } from 'react';

/**
 * Time (ms) to wait for Algonomy recommendations before dismissing the
 * outfitting loading skeleton.
 *
 * The Algonomy `p13n.js` script is consent-gated (OneTrust category C0003).
 * If the shopper declines that category — or the script is otherwise blocked
 * (ad blocker, network failure, Algonomy error) — the loader's `onLoad`
 * callback never fires and `hasLoaded` stays `false` for the whole page view.
 * Without this fallback the outfitting tile would render a perpetual skeleton.
 */
export const OUTFITTING_LOAD_TIMEOUT_MS = 8000;

/**
 * Derives the outfitting tile's loading state from whether Algonomy
 * recommendations have loaded. Resolves to `false` as soon as recommendations
 * load, or after `timeoutMs` if they never do (so the tile can fall back to its
 * empty state instead of an indefinite skeleton).
 */
export default function useOutfittingLoadState(
  hasLoaded: boolean,
  timeoutMs: number = OUTFITTING_LOAD_TIMEOUT_MS
): boolean {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (hasLoaded) return undefined;

    const timer = setTimeout(() => setTimedOut(true), timeoutMs);
    return () => clearTimeout(timer);
  }, [hasLoaded, timeoutMs]);

  return !hasLoaded && !timedOut;
}
